import { gql, useQuery } from '@apollo/client';
import { Button, HStack, Link, Spacer, Stack, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import * as React from 'react';

import { EventActionsMenu } from '@/components/EventActionsMenu';
import { EventActivationStatus } from '@/components/EventActivationStatus';
import { EventAudience } from '@/components/EventAudience';
import { PublishButton } from '@/components/PublishButton';
import { TabLink, TabLinkList } from '@/components/TabLink';
import { VenueFragment } from '@/fragments/venue-fragment';
import { ERegistrationType, EventStatus } from '@/graphql-types/globalTypes';
import { useActivateEvent } from '@/hooks/useActivateEvent';
import { Card } from '@/imports/ui/chakra/components/Card';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  CircleIcon,
} from '@/imports/ui/chakra/feather';
import { Vanilla, VanillaAside, VanillaMain } from '@/layouts/Vanilla';

import {
  GroupSimpleEventTabsLayout_EventQuery,
  GroupSimpleEventTabsLayout_EventQueryVariables,
} from './graphql-types/GroupSimpleEventTabsLayout_EventQuery';

const EVENT = gql`
  query GroupSimpleEventTabsLayout_EventQuery($input: EventInput!) {
    event(input: $input) {
      eventV2 {
        _id
        name
        status
        registrationType
        tickets {
          _id
        }
        ... on SimpleEventV2 {
          venue {
            ...VenueFragment
          }
        }
        ...PublishButton_EventFragment
        ...EventAudience_EventFragment
      }
    }
  }
  ${PublishButton.eventFragment}
  ${EventAudience.eventFragment}
  ${VenueFragment}
`;

export const GroupSimpleEventTabsLayout: React.FC<{
  eventId: string;
  groupId: string;
}> = ({ eventId, groupId, children }) => {
  const queryResult = useQuery<
    GroupSimpleEventTabsLayout_EventQuery,
    GroupSimpleEventTabsLayout_EventQueryVariables
  >(EVENT, {
    variables: { input: { _id: eventId } },
    skip: !eventId,
  });

  const { isLoading, handleEventActivationStatus } = useActivateEvent({
    eventId: eventId,
    isConferenceEvent: false,
    active: queryResult.data?.event?.eventV2?.status === EventStatus.ACTIVE,
  });

  if (queryResult.data?.event.eventV2?.__typename === 'ConferenceEventV2') {
    return (
      <UnexpectedError message="Found unexpected event type: ConferenceEventV2" />
    );
  }

  const eventData = queryResult.data?.event.eventV2;
  const isTicketed = eventData?.registrationType === ERegistrationType.TICKET;

  return (
    <Vanilla>
      <VanillaMain>
        <Stack spacing="4">
          <Stack spacing="4">
            <Stack direction="row" alignItems="center">
              <Link
                as={NextLink}
                href={`/events/${eventId}`}
                display="flex"
                alignItems="center"
                gap="2"
              >
                <ChevronLeftIcon />
                <span>{`Back to ${eventData?.name ?? 'Event'}`}</span>
              </Link>
            </Stack>

            {eventData ? (
              <Stack>
                <Stack direction="row" alignItems="center">
                  <Text textStyle="xlBodyStrong">{eventData.name}</Text>

                  <Spacer />

                  <EventActivationStatus status={eventData.status} />

                  {eventData.status === EventStatus.DRAFT && (
                    <PublishButton
                      event={eventData}
                      isConferenceEvent={false}
                    />
                  )}

                  {eventData.status === EventStatus.INACTIVE && (
                    <Button
                      onClick={handleEventActivationStatus}
                      isLoading={isLoading}
                      size="sm"
                    >
                      Reactivate
                    </Button>
                  )}

                  {(eventData.status === EventStatus.ACTIVE ||
                    eventData.status === EventStatus.DRAFT) && (
                    <EventActionsMenu
                      eventId={eventData._id}
                      eventName={eventData.name}
                      eventStatus={eventData.status}
                      isAdminRoute={false}
                      isEventDuplicable={true}
                      isConferenceEvent={false}
                    />
                  )}
                </Stack>

                <EventAudience event={eventData} isAdminRoute={false} />
              </Stack>
            ) : null}
          </Stack>
          <Card px="6" w="full" mb="4">
            {!eventData ? null : (
              <TabLinkList>
                <TabLink
                  href={`/community/groups/${groupId}/events/${eventId}/overview`}
                >
                  <HStack>
                    {eventData.status === EventStatus.DRAFT && (
                      <CheckCircleIcon color="primary.500" />
                    )}
                    <Text>Overview</Text>
                  </HStack>
                </TabLink>

                {!isTicketed && eventData.status !== EventStatus.DRAFT && (
                  <TabLink
                    href={`/community/groups/${groupId}/events/${eventId}/registrants`}
                  >
                    Registrants
                  </TabLink>
                )}

                {isTicketed && eventData.status !== EventStatus.DRAFT && (
                  <TabLink
                    href={`/community/groups/${groupId}/events/${eventId}/registration`}
                  >
                    Registration
                  </TabLink>
                )}

                {eventData.status !== EventStatus.DRAFT &&
                  eventData.venue.online &&
                  eventData.venue.online.__typename ===
                    'EventVenue_TradewingPlatform' &&
                  eventData.venue.online.stream.__typename ===
                    'EventVenue_TradewingPlatform_Stream_Live' && (
                    <TabLink
                      href={`/community/groups/${groupId}/events/${eventId}/attendance`}
                    >
                      Attendance
                    </TabLink>
                  )}

                {isTicketed && (
                  <TabLink
                    href={`/community/groups/${groupId}/events/${eventId}/tickets`}
                  >
                    <HStack>
                      {eventData.status === EventStatus.DRAFT && (
                        <>
                          {eventData.tickets?.length ? (
                            <CheckCircleIcon color="primary.500" />
                          ) : (
                            <CircleIcon color="gray.300" />
                          )}
                        </>
                      )}
                      <Text>Tickets</Text>
                    </HStack>
                  </TabLink>
                )}

                <TabLink
                  href={`/community/groups/${groupId}/events/${eventId}/recording`}
                >
                  Recording
                </TabLink>

                <TabLink
                  href={`/community/groups/${groupId}/events/${eventId}/email`}
                >
                  Email
                </TabLink>
              </TabLinkList>
            )}
          </Card>
          {children}
        </Stack>
      </VanillaMain>
      <VanillaAside />
    </Vanilla>
  );
};
